import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  writeBatch,
  updateDoc,
  deleteDoc,
  DocumentReference,
  limit,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  ICreateDynamicPaperTopicLessonDto,
  IDynamicPaperTopic,
  IDynamicPaperTopicBase,
  IDynamicPaperTopicFulfilled,
  IDynamicPaperTopicFulfilledWithLessons,
  IDynamicPaperTopicLesson,
  IDynamicPaperTopicRaw,
  IExamResource,
  IGenericError,
  ILessonRaw,
  TLessonVariant,
} from '../types'
import { getExamResourcesListByIds } from './exam-resources'
import { questionNumberNumericSort } from '@/utils/dynamic-paper'

const dynamicPaperTopicsCollectionName = 'dynamicPaperTopics'
const dynamicPaperTopicsLessonsSubCollectionName = 'lessons'

const dynamicPaperTopicsCollectionRef = collection(
  firebaseDb,
  dynamicPaperTopicsCollectionName,
)

const dynamicPaperTopicsDocRef = (id: string) =>
  doc(firebaseDb, dynamicPaperTopicsCollectionName, id)

/**
 * Create a new dynamic paper topic
 */
export async function createDynamicPaperTopic(
  topicData: IDynamicPaperTopicBase,
): Promise<string> {
  try {
    const collectionRef = collection(
      firebaseDb,
      dynamicPaperTopicsCollectionName,
    )

    const docRef = await addDoc(collectionRef, {
      ...topicData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    return docRef.id
  } catch (error: unknown) {
    console.error(`error while trying to create dynamic paper topic :>> `, {
      message: (error as IGenericError)?.message,
      error,
      topicData,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to create dynamic paper topic`,
    )
  }
}

export const getDynamicPaperTopicById = async (
  id: string,
): Promise<IDynamicPaperTopic | undefined> => {
  try {
    const docRef = dynamicPaperTopicsDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IDynamicPaperTopicRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IDynamicPaperTopic = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while tying to get dynamicPaperTopic by id: ${id} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )
  }
}

export async function getDynamicPaperTopics(
  queryConstraints: QueryConstraint[] = [],
): Promise<IDynamicPaperTopic[]> {
  try {
    let q = query(dynamicPaperTopicsCollectionRef, ...queryConstraints)

    const querySnapshot = (await getDocs(
      q,
    )) as QuerySnapshot<IDynamicPaperTopicRaw>

    let documents: IDynamicPaperTopic[] = []

    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data(),
      })
    })

    return documents
  } catch (error: unknown) {
    console.error(`error while trying to get dynamicPaperTopics :>> `, {
      message: (error as IGenericError)?.message,
      error,
      queryConstraints,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopics`,
    )
  }
}

export async function getDynamicPaperTopicFulfilledById(
  id: string,
): Promise<IDynamicPaperTopicFulfilled> {
  try {
    const topic = await getDynamicPaperTopicById(id)

    if (!topic) {
      throw new Error(`Topic: ${id} not found`)
    }

    const questions: IExamResource[] = await getExamResourcesListByIds(
      topic.questionsIds,
    )

    return { ...topic, questions }
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopic fulfilled :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        id,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopic with questions`,
    )
  }
}

export async function getDynamicPaperTopicsByIds(
  ids: string[],
): Promise<IDynamicPaperTopicFulfilled[]> {
  try {
    const topics: IDynamicPaperTopicFulfilled[] = await Promise.all(
      ids.map(async (id) => {
        return await getDynamicPaperTopicFulfilledById(id)
      }),
    ).then((data) =>
      // filter out undefined values
      data.filter((t): t is IDynamicPaperTopicFulfilled => t !== undefined),
    )

    return topics
  } catch (error: unknown) {
    console.error(`error while trying to get examResources list :>> `, {
      message: (error as IGenericError)?.message,
      error,
      ids,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get examResources list.`,
    )
  }
}

export async function saveDynamicPaperTopicLesson(
  topicId: string,
  lessonId: string,
  dto: ICreateDynamicPaperTopicLessonDto,
  removeVariantBeforeSave?: boolean,
): Promise<void> {
  try {
    const { variant, ...createObj } = dto

    // lesson path
    const lessonPath = `${dynamicPaperTopicsCollectionName}/${topicId}/${dynamicPaperTopicsLessonsSubCollectionName}/${lessonId}`
    const lessonDocRef = doc(firebaseDb, lessonPath)

    // variant path
    const variantPath = `${lessonPath}/variants`
    const variantCollectionRef = collection(firebaseDb, variantPath)

    if (removeVariantBeforeSave) {
      // clear existing variants
      const existingVariantsSnapshot = await getDocs(variantCollectionRef)
      const batch = writeBatch(firebaseDb)

      existingVariantsSnapshot.docs.forEach((docSnap) => {
        batch.delete(docSnap.ref)
      })

      await batch.commit()
    }

    // save lesson
    await setDoc(lessonDocRef, {
      ...createObj,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    // save variant
    await addDoc(variantCollectionRef, {
      ...variant,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while trying to save dynamic paper topic lesson :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        topicId,
        lessonId,
        dto,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to save dynamic paper topic lesson.`,
    )
  }
}

export async function getDynamicPaperTopicsWithLessonsByIds(
  ids: string[],
): Promise<IDynamicPaperTopicFulfilledWithLessons[]> {
  try {
    // Deduplicate IDs to prevent showing same topic multiple times
    const uniqueIds = [...new Set(ids)]
    const topics: IDynamicPaperTopicFulfilledWithLessons[] = await Promise.all(
      uniqueIds.map(async (id) => {
        return await getDynamicPaperTopicFulfilledWithLessonsById(id)
      }),
    ).then((data) =>
      // filter out undefined values
      data.filter(
        (t): t is IDynamicPaperTopicFulfilledWithLessons => t !== undefined,
      ),
    )

    return topics
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopics fulfilled with lessons list:>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        ids,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopics fulfilled with lessons list.`,
    )
  }
}

export async function getDynamicPapersTopicLessonsByIds(
  topicId: string,
  lessonIds: string[],
): Promise<IDynamicPaperTopicLesson[]> {
  try {
    const lessons: IDynamicPaperTopicLesson[] = await Promise.all(
      lessonIds.map(async (lessonId) => {
        return (await getDynamicPapersLessonById(topicId, lessonId)) ?? null
      }),
    ).then((results) =>
      results.filter(
        (lesson): lesson is IDynamicPaperTopicLesson => lesson !== null,
      ),
    )

    return lessons.sort((a, b) =>
      questionNumberNumericSort(a.questionNumber!, b.questionNumber!),
    )
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopic lesson by id :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        topicId,
        lessonIds,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopic lesson`,
    )
  }
}

export async function getDynamicPaperTopicFulfilledWithLessonsById(
  id: string,
): Promise<IDynamicPaperTopicFulfilledWithLessons> {
  try {
    console.log(`[Topic Fetch] Fetching topic with lessons: ${id}`)

    const topic = await getDynamicPaperTopicById(id)

    if (!topic) {
      throw new Error(`Topic: ${id} not found`)
    }

    console.log(`[Topic Fetch] Topic found:`, {
      topicId: id,
      topicName: topic.topicName,
      questionsIds: topic.questionsIds,
      questionsCount: topic.questionsIds?.length || 0,
    })

    const lessons: IDynamicPaperTopicLesson[] =
      await getDynamicPapersTopicLessonsByIds(id, topic.questionsIds || [])

    console.log(
      `[Topic Fetch] ✅ Topic with ${lessons.length} lessons loaded: ${topic.topicName}`,
    )

    return { ...topic, lessons }
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopic fulfilled with lessons :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        id,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopic with lessons`,
    )
  }
}

export async function getDynamicPapersLessonById(
  topicId: string,
  lessonId: string,
): Promise<IDynamicPaperTopicLesson | undefined> {
  try {
    const lessonRef = doc(
      firebaseDb,
      dynamicPaperTopicsCollectionName,
      topicId,
      dynamicPaperTopicsLessonsSubCollectionName,
      lessonId,
    )

    const lessonSnap = (await getDoc(
      lessonRef,
    )) as unknown as DocumentSnapshot<ILessonRaw>

    if (!lessonSnap.exists()) {
      return undefined
    }

    const lesson: IDynamicPaperTopicLesson = {
      id: lessonSnap.id,
      ...lessonSnap.data(),
    }

    return lesson
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopic lessons by ids :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        topicId,
        lessonId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopic lessons`,
    )
  }
}

export async function getDynamicPapersLessonVariantsById<T = TLessonVariant>(
  topicId: string,
  lessonId: string,
): Promise<T[]> {
  try {
    const variantsSubCollectionRef = collection(
      firebaseDb,
      dynamicPaperTopicsCollectionName,
      topicId,
      dynamicPaperTopicsLessonsSubCollectionName,
      lessonId,
      'variants',
    )

    const querySnapshot = (await getDocs(
      variantsSubCollectionRef,
    )) as unknown as QuerySnapshot<T>

    const variants: T[] = querySnapshot.docs.map((doc) => {
      const variant: T = { id: doc.id, ...doc.data() }

      return variant
    })

    return variants
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopic lessons variants :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        topicId,
        lessonId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopic lessons variants`,
    )
  }
}

export async function getDynamicPapersLessonVariantById<T = TLessonVariant>(
  topicId: string,
  lessonId: string,
  variantId: string,
): Promise<T | undefined> {
  try {
    const variantDocRef = doc(
      firebaseDb,
      dynamicPaperTopicsCollectionName,
      topicId,
      dynamicPaperTopicsLessonsSubCollectionName,
      lessonId,
      'variants',
      variantId,
    )

    const snapshot = await getDoc(variantDocRef)

    if (!snapshot.exists) {
      return undefined
    }

    const variant: T = { id: snapshot.id, ...snapshot.data() } as T

    return variant
  } catch (error: unknown) {
    console.error(
      `error while trying to get dynamicPaperTopic lessons variant by id :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        topicId,
        lessonId,
        variantId,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get dynamicPaperTopic lessons variant`,
    )
  }
}

export async function updateDynamicPapersLessonVariantById<T = TLessonVariant>(
  topicId: string,
  lessonId: string,
  variantId: string,
  updateObj: Partial<T>,
): Promise<void> {
  try {
    const variantDocRef = doc(
      firebaseDb,
      dynamicPaperTopicsCollectionName,
      topicId,
      dynamicPaperTopicsLessonsSubCollectionName,
      lessonId,
      'variants',
      variantId,
    )

    await updateDoc(variantDocRef, {
      ...updateObj,
      updatedAt: serverTimestamp(),
    })
  } catch (error: unknown) {
    console.error(
      `error while trying to update dynamicPaperTopic lessons variant by id :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
        topicId,
        lessonId,
        updateObj,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update dynamicPaperTopic lessons variant`,
    )
  }
}

export async function updateDynamicPaperTopicById(
  id: string,
  updateObj: Partial<IDynamicPaperTopicRaw>,
): Promise<void> {
  try {
    const docRef = dynamicPaperTopicsDocRef(id)

    await updateDoc(docRef, { ...updateObj, updatedAt: serverTimestamp() })
  } catch (error: unknown) {
    console.error(
      `error while trying to update dynamic paper topic by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
      updateObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update dynamic paper topic`,
    )
  }
}

export async function deleteDynamicPaperTopic(topicId: string): Promise<void> {
  try {
    const docRef = dynamicPaperTopicsDocRef(topicId)

    // delete all lessons
    await deleteDpLessonsSubCollection(docRef)

    // delete the parent topic document
    await deleteDoc(docRef)
  } catch (error: unknown) {
    console.error(`error while trying to delete dynamic paper topic :>> `, {
      message: (error as IGenericError)?.message,
      error,
      topicId,
    })

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete dynamic paper topic`,
    )
  }
}

async function deleteDpLessonsSubCollection(
  parentDocRef: DocumentReference,
): Promise<void> {
  const lessonsSubcollectionRef = collection(
    parentDocRef,
    dynamicPaperTopicsLessonsSubCollectionName,
  )

  // maximum batch size for Firestore is 500
  const batchSize = 250
  let hasMoreLessons = true

  while (hasMoreLessons) {
    // Get a batch of documents
    const lessonsSnapshot = await getDocs(
      query(lessonsSubcollectionRef, limit(batchSize)),
    )

    // if no documents left, we're done here
    if (lessonsSnapshot.empty) {
      hasMoreLessons = false
      continue
    }

    // delete documents in batch
    const batch = writeBatch(firebaseDb)
    lessonsSnapshot.forEach((doc) => {
      batch.delete(doc.ref)
    })

    await batch.commit()

    // if we got less documents than the batch size, we're done here
    if (lessonsSnapshot.size < batchSize) {
      hasMoreLessons = false
    }
  }
}
